import { useState, useEffect } from "react";
import MainLogo from "../img/LogoO@2x.png";
import MainLogoPhone from "../img/LogoO@2x.png";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Link as ScrollLink } from "react-scroll";
import { CloseIcon, HamburgerIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  IconButton,
  Image,
  Stack,
  useBreakpointValue,
  useDisclosure,
  Portal,
} from "@chakra-ui/react";

export default function Nav() {
  const mainColor = "#fff";
  const accentColor = "#c6a664";
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { i18n, t } = useTranslation();
  const imageSrc = useBreakpointValue({ base: MainLogoPhone, md: MainLogo });
  const location = useLocation();
  const isHome = location.pathname === "/";

  const [isScrolled, setIsScrolled] = useState(false);

  // Detect scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 80) setIsScrolled(true);
      else setIsScrolled(false);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleLang = () => {
    const newLang = i18n.language === "ar" ? "en" : "ar";
    i18n.changeLanguage(newLang);
    document.dir = newLang === "ar" ? "rtl" : "ltr";
  };

  const menuItems = [
    { label: t("navbar.home"), to: "hero" },
    { label: t("navbar.about"), to: "overview" },
    { label: t("navbar.visionMission"), to: "vision-mission" },
    { label: t("navbar.businesses"), to: "businesses" },
    { label: t("navbar.careers"), to: "careers" },
    { label: t("navbar.contact"), to: "contact" },
  ];

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      w="100%"
      zIndex="1000"
      transition="all 0.4s ease"
      bg={isScrolled ? "rgba(0,0,0,0.95)" : "transparent"}
      boxShadow={isScrolled ? "0px 2px 10px rgba(0,0,0,0.4)" : "none"}
      backdropFilter={isScrolled ? "blur(6px)" : "none"}
    >
      <Container maxW="container.xl" py={2}       mt={'1'}>
        <Flex alignItems="center" justifyContent="space-between">
          {/* Mobile Menu Button */}
          <IconButton
            aria-label="Toggle Menu"
            bg="transparent"
            color="white"
            icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
            onClick={isOpen ? onClose : onOpen}
            display={{ base: "block", md: "none" }}
            _hover={{ bg: "rgba(255,255,255,0.1)" }}
          />

          {/* Logo */}
          <Flex align="center" gap={4} >
            <Image
              src={imageSrc}
              alt="Alpha United Investment Logo"
              h={useBreakpointValue({ base: "45px", md: "60px", lg: "70px" })}
              w="auto"
              objectFit="contain"
              // filter="drop-shadow(0px 0px 3px rgba(255,255,255,0.3))"
            />
          </Flex>

          {/* Desktop Menu */}
          <HStack
            as="nav"
            spacing={4}
            display={{ base: "none", md: "flex" }}
            align="center"
          >
        {menuItems.map((item, index) => (
  <ScrollLink
    key={index}
    to={item.to}
    smooth
    duration={500}
    offset={-80}
  >
    <Button
      variant="outline"
      border="2px solid transparent"
      borderColor="transparent"
      fontWeight="medium"
      color={mainColor}
      _hover={{
        borderColor: accentColor,
        color: accentColor,
        bg: "transparent",
      }}
      fontSize="md"
      bg="transparent"
      transition="all 0.3s ease"
    >
      {item.label}
    </Button>
  </ScrollLink>
))}


            {/* Language Toggle */}
            <Button
              size="md"
              variant="outline"
              borderColor={accentColor}
              color={accentColor}
              _hover={{ bg: accentColor, color: "black" }}
              onClick={toggleLang}
            >
              {i18n.language === "ar" ? "English" : "عربي"}
            </Button>
          </HStack>

          {/* Mobile Language Button */}
          <HStack spacing={6}>
            <Button
              display={{ base: "block", md: "none" }}
              size="sm"
              variant="outline"
              borderColor={accentColor}
              color={accentColor}
              _hover={{ bg: accentColor, color: "black" }}
              onClick={toggleLang}
            >
              {i18n.language === "ar" ? "English" : "عربي"}
            </Button>
          </HStack>
        </Flex>

        {/* Mobile Menu Overlay */}
        {isOpen && (
          <Portal>
            <Box
              bg="rgba(0,0,0,0.95)"
              position="fixed"
              top="70px"
              left="0"
              w="100%"
              py={4}
              px={4}
              boxShadow="xl"
              zIndex={9999}
              transition="all 0.3s ease"
            >
              <Stack spacing={3}>
                {menuItems.map((item, index) => (
                  <ScrollLink
                    key={index}
                    to={item.to}
                    smooth
                    duration={500}
                    offset={-80}
                    onClick={onClose}
                  >
                    <Button
                      variant="ghost"
                      fontWeight="medium"
                      color="white"
                      _hover={{ color: accentColor }}
                      w="100%"
                      fontSize="lg"
                    >
                      {item.label}
                    </Button>
                  </ScrollLink>
                ))}
              </Stack>
            </Box>
          </Portal>
        )}
      </Container>
    </Box>
  );
}
